import { Graph } from '@dagrejs/graphlib';
import { v4 as uuidv4 } from 'uuid';

const GRAPH_OPTIONS = { multigraph: true, directed: true };
export const GROUP_STEP_PREFIX = 'group-'; // Group step, embodies multiple steps
export const ADD_STEP_PREFIX = 'add-step-'; // Node to click and add an action
export const HIDDEN_STEP_PREFIX = 'hidden-'; // Invisible node, no lines in or out
export const PASSTHROUGH_STEP_PREFIX = 'passthrough-'; // Invisible node, lines in and out but no arrow
export const END_STEP_ID = 'end-step';
export const EXPANDED_GROUP_START_PREFIX = 'expanded-group-start-';
export const EXPANDED_GROUP_END_PREFIX = 'expanded-group-end-';
export const SNIPPET_PREFIX = 'snippet-';
export const SNIPPET_STARTING_ID = 'Snippet-';

export interface Edge {
  v: string; // NodeID of the beginning of the edge
  w: string; // NodeID of the end of the edge
  name?: string; // Name of the edge (optional)
}

export interface GraphNode {
  stepID: string; // ID of the node in the graph
  childrenStepIDs: string[]; // The ID of the nodes that this node points to
  terminatingStep: boolean; // An action that stops the automation visually (End, Jump, etc.)
  groupId: string; // Graph util supports grouping nodes
  expandedGroup?: boolean;
}

export interface TracedBranchNode {
  nodeId: string; // The node ID of the branching node
  numChildren: number; // How many children (branches) it has
  indexWeCameFrom: number; // The path we came up to land on this branching node
}

export interface Group {
  topNode: string;
  bottomNodes: string[];
}

// AutomationGraph is responsible for creating a graph representation of the automation for our graphing library
// It will take in the nodes and their connections and create a graph that can be used to draw the automation
// It will also calculate some additional information that we use for drawing the automation
export class AutomationGraph {
  // Docs @ https://github.com/dagrejs/graphlib/wiki/API-Reference#graph-api
  private graph: Graph = new Graph(GRAPH_OPTIONS);
  public rootBranchingNodes = new Set<string>();
  public terminatingStepIds: string[] = [];
  // Because nodes in a group are removed from the graph we keep track of group data separately
  public groupData: Map<string, Group> = new Map();

  constructor(nodes: GraphNode[]) {
    this.buildGraph([...nodes]);
  }

  // Give this the actions and which actions they go to. It will translate that into the graphing library
  // and add all nodes and edges
  private buildGraph(nodes: GraphNode[]): Graph {
    this.graph = new Graph(GRAPH_OPTIONS);
    this.graph.setNode(END_STEP_ID);
    this.rootBranchingNodes = new Set<string>();

    // First pass in building up the graph
    this.constructGraphGivenNodes(nodes);

    // Add expanded group containers
    this.constructExpandedGroupContainers(nodes);

    // Put Add Steps on edges
    this.constructAddSteps();

    // Compile the steps that are terminating (Jump, End, etc.)
    this.calculateTerminatingSteps();

    // Calculate root branching nodes, used when constructing the terminating steps
    this.calculateRootBranchingNodes();

    // Figure out where the terminating steps are supposed to point to (or would point to if they weren't terminating)
    this.constructTerminatingSteps();

    // We have all the nodes and where they go to, now we need to handle the merging of branches
    this.constructMergingSteps();

    // Calculate root branching nodes again, used when drawing / placing nodes.
    // Needs to be calculated again after merging steps are added
    this.calculateRootBranchingNodes();

    // If all leafs are terminating steps, then we can remove the end step so that it is not drawn
    this.optionallyRemoveEndStep();

    return this.graph;
  }

  private constructGraphGivenNodes(nodes: GraphNode[]): void {
    // For each node we are given, create it in our graphing library
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      // We don't know where End and Jump steps are meant to go until the rest of the graph is made
      if (node.terminatingStep) {
        continue;
      }

      // For each of the nodes children, create an edge between it and the parent
      for (let childStepID of node.childrenStepIDs) {
        // Empty string means "no next step", which means it goes to the end step
        if (childStepID === '') {
          childStepID = END_STEP_ID;
        }

        let childNode = nodes.find((n) => n.stepID === childStepID);
        if ((node.groupId && !node.expandedGroup) || (childNode && childNode.groupId && !childNode.expandedGroup)) {
          // If either the parent or child are a part of a group, handle the group separately
          childNode = childNode || {
            stepID: childStepID,
            childrenStepIDs: [],
            terminatingStep: false,
            groupId: '',
            expandedGroup: false,
          };
          this.handleNodeBeingInAGroup(node, childNode);
          continue;
        }

        const edgeID = uuidv4();
        this.graph.setNode(node.stepID);
        this.graph.setNode(childStepID);
        this.graph.setEdge(node.stepID, childStepID, edgeID, edgeID);
      }
    }
  }

  private constructExpandedGroupContainers(nodes: GraphNode[]): void {
    const startPlaced = new Map<string, boolean>();
    const endPlaced = new Map<string, boolean>();

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (!node.groupId || !node.expandedGroup) {
        // Collapsed nodes are already handled in constructGraphGivenNodes
        continue;
      }

      // If the node is the first node in the group
      const parentNodesIds = this.getParentNodes(node.stepID) || [];
      for (let i = 0; i < parentNodesIds?.length; i++) {
        const parentNodeId = parentNodesIds[i];
        const parentNode = nodes.find((n) => n.stepID === parentNodeId);
        if (node.groupId !== parentNode?.groupId && !startPlaced.get(node.groupId)) {
          // The node is the first node in the group
          const expandedGroupStepId = this.generateExpandedGroupStepId(node.groupId, true);
          this.addExpandedGroupStartStep(node.stepID, expandedGroupStepId);
          startPlaced.set(node.groupId, false);
          break;
        }
      }

      // If the node is the last node in the group
      const firstChildNode = nodes.find((n) => n.stepID === node.childrenStepIDs[0]);
      if (node.groupId !== firstChildNode?.groupId && !endPlaced.get(node.groupId) && !node.terminatingStep) {
        // The node is the last node in the group
        const expandedGroupStepId = this.generateExpandedGroupStepId(node.groupId, false);
        this.addExpandedGroupEndStep(
          this.getChildrenNodes(node.stepID)?.[0] || END_STEP_ID,
          expandedGroupStepId,
          node.groupId,
          nodes,
        );
        endPlaced.set(node.groupId, true);
      }
    }
  }

  /**
   * This function will place a new node of expandedGroupStepId in the graph, for the end of the group, above the node
   */
  private addExpandedGroupEndStep(
    childStepId: string,
    expandedGroupStepId: string,
    groupId: string,
    nodes: GraphNode[],
  ) {
    this.graph.setNode(expandedGroupStepId);
    this.addStepWithoutChangingOrder(childStepId, expandedGroupStepId, groupId, nodes);
    this.graph.setEdge(expandedGroupStepId, childStepId, '', '');
  }

  /**
   * This function will place a new node of expandedGroupStepId in the graph, for the beginning of the group, above the node
   */
  private addExpandedGroupStartStep(stepId: string, expandedGroupStepId: string) {
    this.graph.setNode(expandedGroupStepId);
    this.addStepWithoutChangingOrder(stepId, expandedGroupStepId);
    this.graph.setEdge(expandedGroupStepId, stepId, '', '');
  }

  // Adds a new step at step ID without changing the order of the edges
  private addStepWithoutChangingOrder(
    stepId: string,
    newStepId: string,
    onlyChangeEdgesForThisGroup?: string,
    nodesForFindingGroup?: GraphNode[],
  ) {
    // The order in which edges are created affects the order we get when we call graphV2.edges()
    // We don't want to change the order here, so we will remove the edges and add them back in the correct order
    const inEdges = this.graph.inEdges(stepId) || [];
    inEdges.forEach((parentEdge: Edge) => {
      const parentOutEdges = this.graph.outEdges(parentEdge.v) || [];
      const indexOfReplacement = parentOutEdges?.findIndex((edge: Edge) => edge.w === stepId);

      // remove all edges from the parent to the step
      for (const edge of parentOutEdges) {
        this.graph.removeEdge(edge.v, edge.w, edge.name || '');
      }

      // Put the edges back with the change being in the correct position
      for (let i = 0; i < parentOutEdges.length; i++) {
        const edge = parentOutEdges[i];
        const edgeGroupId = nodesForFindingGroup?.find((n) => n.stepID === parentEdge.v)?.groupId;
        if (!onlyChangeEdgesForThisGroup) {
          if (i === indexOfReplacement) {
            this.graph.setEdge(parentEdge.v, newStepId, parentEdge.name || '', parentEdge.name || '');
          } else {
            this.graph.setEdge(edge.v, edge.w, edge.name || '', edge.name || '');
          }
        } else {
          // When adding an end group step, we only want to place the end group step in the middle of the edges that are part of the group. If we don't
          // do this then it's possible to accidentally add extra nodes to the expanded group visualization (even though those nodes aren't actually in
          // the group).
          if (i === indexOfReplacement && edgeGroupId === onlyChangeEdgesForThisGroup) {
            this.graph.setEdge(parentEdge.v, newStepId, parentEdge.name || '', parentEdge.name || '');
          } else {
            this.graph.setEdge(edge.v, edge.w, edge.name || '', edge.name || '');
          }
        }
      }
    });
  }

  private handleNodeBeingInAGroup(parentNode: GraphNode, childNode: GraphNode): void {
    if (parentNode.groupId === childNode.groupId) {
      // if both the parent and child are in the same group, do nothing
      return;
    }

    const parentNodeId = this.buildGroupStepId(parentNode.groupId, parentNode.expandedGroup) || parentNode.stepID;
    const childNodeId = this.buildGroupStepId(childNode.groupId, childNode.expandedGroup) || childNode.stepID;

    const parentGroup = this.groupData.get(parentNodeId) || { topNode: '', bottomNodes: [] };
    const childGroup = this.groupData.get(childNodeId) || { topNode: '', bottomNodes: [] };

    let edgeId = uuidv4();
    if (parentNode.groupId && !parentNode.expandedGroup) {
      // if the parent is in the group don't make a unique edge (resulting in 1 edge out from the group)
      edgeId = '';
      // Keep track of the bottom node of this group
      parentGroup.bottomNodes.push(parentNode.stepID);
      this.groupData.set(parentNodeId, parentGroup);
    }
    if (childNode.groupId && !childNode.expandedGroup) {
      // keep track of the top node for this group
      childGroup.topNode = childNode.stepID;
      this.groupData.set(childNodeId, childGroup);
    }

    this.graph.setNode(parentNodeId);
    this.graph.setNode(childNodeId);
    this.graph.setEdge(parentNodeId, childNodeId, edgeId, edgeId);
  }

  private constructAddSteps(): void {
    // every edge in the graph needs an AddStep between them
    const allEdges = this.getAllEdges();
    for (let i = 0; i < allEdges.length; i++) {
      const edge = allEdges[i];
      const addStepID = this.generateAddStepId();
      this.graph.setNode(addStepID);
      this.graph.removeEdge(edge.v, edge.w, edge?.name || '');
      this.graph.setEdge(edge.v, addStepID);
      this.graph.setEdge(addStepID, edge.w);
    }
  }

  private calculateTerminatingSteps(): void {
    // Terminating steps are any node in the graph that has no child except for the end step
    this.terminatingStepIds = this.graph
      .nodes()
      .filter((node) => this.getChildrenNodes(node)?.length === 0 && node !== END_STEP_ID);
  }

  // Terminating steps that are inside a branch need an edge to the node that will be merged on
  // This edge won't be visible, but is needed to get proper spacing when plotting the graph
  private constructTerminatingSteps(): void {
    for (let i = 0; i < this.terminatingStepIds.length; i++) {
      const step = this.terminatingStepIds[i];
      const mergeStep = this.getMyBranchMergeStep(step);
      this.graph.setEdge(step, mergeStep);
    }
  }

  private getMyBranchMergeStep(nodeId: string, parentToFollow?: string): string {
    const branchNode = this.traceNodeUpToTheBranchNodeIAmInside(nodeId, 0, false, parentToFollow);
    if (!branchNode) {
      return END_STEP_ID;
    }
    return this.getMergeStepForBranch(branchNode.nodeId, true, branchNode.indexWeCameFrom);
  }

  // We have all the nodes and where they go to, now we need to handle the merging of branches
  // and place the AddStep at those merges
  private constructMergingSteps(): void {
    let numberOfBranchingNodes = 0;
    // Make sure all the branching nodes are in the map
    for (let i = 0; i < this.getAllNodes()?.length; i++) {
      const node = this.getAllNodes()[i];
      const children = this.getChildrenNodes(node);
      if (!children || children.length <= 1) {
        continue;
      }
      numberOfBranchingNodes++;
    }

    const finishedBranchingNodes = new Set<string>();
    const mergeStepsToIgnore = new Set<string>();

    while (finishedBranchingNodes.size !== numberOfBranchingNodes) {
      const parentBranchingNodeIdsMap = new Map<string, { numChildren: number; childrenFound: string[] }>();
      const allNodes = this.getAllNodes();

      // populate parentBranchingNodeIdsMap with the children that are already known
      for (let i = 0; i < allNodes?.length; i++) {
        const node = allNodes[i];
        const parentNodes = this.getParentNodes(node);
        if (!parentNodes || parentNodes.length <= 1 || mergeStepsToIgnore.has(node)) {
          // If the node does not have multiple parents then there is nothing to merge
          continue;
        }
        for (let j = 0; j < parentNodes.length; j++) {
          const parent = parentNodes[j];

          const parentBranchingNode = this.traceNodeUpToFirstBranchingNodeNotInSet(
            parent,
            mergeStepsToIgnore,
            finishedBranchingNodes,
          );
          if (!parentBranchingNode) {
            continue;
          }

          const existingValue = parentBranchingNodeIdsMap.get(parentBranchingNode.nodeId);
          const accumulatedChildren = (existingValue?.childrenFound || []).concat([parent]);
          parentBranchingNodeIdsMap.set(parentBranchingNode.nodeId, {
            numChildren: parentBranchingNode.numChildren,
            childrenFound: accumulatedChildren,
          });
        }
      }

      // merge a single branch
      let parentBranchingNodeId = '';
      for (const [key, value] of parentBranchingNodeIdsMap) {
        if (value.numChildren === value.childrenFound.length) {
          parentBranchingNodeId = key;
          break;
        }
      }
      const parentBranchingNode = parentBranchingNodeIdsMap.get(parentBranchingNodeId);
      if (!parentBranchingNode) {
        console.error(
          'parentBranchingNodeIdsMap is not empty but no branch has all of its children',
          parentBranchingNodeIdsMap,
        );
        break;
      }

      const terminatingOrHiddenChildren = parentBranchingNode.childrenFound.filter((stepId) => {
        return this.terminatingStepIds.includes(stepId) || stepId.startsWith(HIDDEN_STEP_PREFIX);
      });

      // Merge the steps
      let addStepID = this.generateAddStepId();
      if (terminatingOrHiddenChildren.length === parentBranchingNode.childrenFound.length) {
        // If all the children are end steps, merge it with an invisible node that has no lines
        addStepID = this.generateHiddenStepId();
      } else if (terminatingOrHiddenChildren.length === parentBranchingNode.childrenFound.length - 1) {
        // If all the children but one are end steps, merge it with an invisible node that has lines
        addStepID = this.generatePassthroughStepId();
      }

      const newStepsChild = this.getNewStepsChild(parentBranchingNode.childrenFound);
      this.addNodeBetweenChildAndParents(addStepID, newStepsChild, parentBranchingNode.childrenFound);
      // Merge is done. Add it to set so it is not considered again
      finishedBranchingNodes.add(parentBranchingNodeId);
      mergeStepsToIgnore.add(addStepID);

      // Re-calculate the terminating steps now that changes to the graph have occurred
      for (const termStep of this.terminatingStepIds) {
        const myBranchMergeStep = this.getMyBranchMergeStep(termStep, addStepID);
        if (!myBranchMergeStep) {
          continue;
        }
        // Remove whatever the current edge is and set it to whatever we found
        this.graph.removeEdge(termStep, this.getChildrenNodes(termStep)?.[0] || '');
        this.graph.setEdge(termStep, myBranchMergeStep);
      }
    }
  }

  private getNewStepsChild(childrenFound: string[]): string {
    if (!childrenFound?.length) {
      return '';
    }
    // Hidden steps tend to be less trustworthy because calculating terminating steps is hard. Ideally we wouldn't need this.
    const nonHiddenStep = childrenFound.find((stepId) => !stepId.startsWith(HIDDEN_STEP_PREFIX));
    if (nonHiddenStep) {
      return this.getChildrenNodes(nonHiddenStep)?.[0] || '';
    }
    return this.getChildrenNodes(childrenFound[0])?.[0] || '';
  }

  private addNodeBetweenChildAndParents(newNodeId: string, originalChildId: string, parentIds: string[]): void {
    this.graph.setNode(newNodeId);
    parentIds.forEach((parent) => {
      this.graph.removeEdge(parent, this.getChildrenNodes(parent)?.[0] || '');
      this.graph.setEdge(parent, newNodeId);
    });
    this.graph.setEdge(newNodeId, originalChildId);
  }

  // traceNodeUpToFirstBranchingNode will follow the parent(s) of a node until it hits a branching node (if/else, a/b, delay until, etc.)
  private traceNodeUpToFirstBranchingNodeNotInSet(
    nodeId: string,
    mergeStepsToTraverse: Set<string>,
    branchStepsToIgnore: Set<string>,
  ): TracedBranchNode | null {
    let current = nodeId;
    // for (;;) is the same as while(true) but without a lint error
    for (;;) {
      const parents = this.getParentNodes(current) || [];
      if (parents?.length > 1 && !mergeStepsToTraverse.has(current)) {
        // we hit a merge step we can't go beyond yet
        return null;
      }

      const childrenNodes: string[] = this.getChildrenNodes(current) || [];
      if (childrenNodes?.length > 1 && !branchStepsToIgnore.has(current)) {
        return {
          nodeId: current,
          numChildren: childrenNodes.length,
          indexWeCameFrom: childrenNodes.indexOf(nodeId),
        };
      }

      if (!parents?.length) {
        return null; // We have reached the top of the graph
      }
      current = parents[0];
    }
  }

  // traceNodeUpToFirstBranchingNode will follow the parent(s) of a node until it hits a branching node (if/else, a/b, delay until, etc.)
  public traceNodeUpToFirstBranchingNode(nodeId: string): TracedBranchNode | null {
    let current = nodeId;
    // for (;;) is the same as while(true) but without a lint error
    for (;;) {
      const childrenNodes: string[] = this.getChildrenNodes(current);
      if (childrenNodes?.length > 1) {
        return { nodeId: current, numChildren: childrenNodes.length, indexWeCameFrom: childrenNodes.indexOf(nodeId) };
      } else {
        const parents = this.getParentNodes(current);
        if (!parents?.length) {
          return null; // We have reached the top of the graph
        }
        current = parents[0];
      }
    }
  }

  // getMergeStepForBranch will follow the children of a branching node to find the step it merges on
  // childIndexToIgnore is used to not go down a particular branch. Ex: If it's the branch we came from and know it doesn't need to be followed
  // canTraceUp is used to avoid infinite loops from backtracking back up to a parent branch that we already came from.
  // When false it will not go up to its parent branch to try and find a merge step
  public getMergeStepForBranch(nodeId: string, canTraceUp: boolean, childIndexToIgnore?: number): string {
    const children = this.getChildrenNodes(nodeId);
    if (!children?.length) {
      return END_STEP_ID;
    }
    for (let i = 0; i < children.length; i++) {
      if (i === childIndexToIgnore) {
        continue;
      }
      const child = children[i];
      const mergeStep = this.traceDownThroughPathForMergeStep(child);
      if (mergeStep) {
        return mergeStep;
      }
    }
    // Depending on the use of terminating steps, it's possible the merge step can only be found by backtracking
    // up to the previous branch step. But will never be above a root branching node.
    if (canTraceUp && !this.rootBranchingNodes.has(nodeId)) {
      const higherParent = this.traceNodeUpToTheBranchNodeIAmInside(nodeId);
      if (higherParent?.nodeId) {
        return this.getMergeStepForBranch(higherParent.nodeId, true, higherParent.indexWeCameFrom);
      }
    }

    // If there was no merge step found, then we must be converging on the very last step with multiple parents
    return END_STEP_ID;
  }

  // A merge step is a node with multiple parents, follow the children nodes down until you find one
  private traceDownThroughPathForMergeStep(nodeId: string): string {
    if (nodeId.startsWith(EXPANDED_GROUP_END_PREFIX)) {
      // If we hit the end of a group, the merge should happen before the group ends.
      return nodeId;
    }
    const parents = this.getParentNodes(nodeId) || [];
    if (parents?.length > 1) {
      return nodeId;
    }
    const children = this.getChildrenNodes(nodeId);
    if (!children?.length) {
      return '';
    } else if (children.length === 1) {
      return this.traceDownThroughPathForMergeStep(children[0]);
    }
    // We hit a branch node. The merge node we are looking for will be beyond this inner branch's merge node
    // canTraceUp is false because we would have come from our parent branch to get here
    const innerMergeStepId = this.getMergeStepForBranch(nodeId, false);
    if (innerMergeStepId) {
      return this.traceDownThroughPathForMergeStep(this.getChildrenNodes(innerMergeStepId)?.[0] || '');
    }
    // We could not find a merge step
    return '';
  }

  private calculateRootBranchingNodes(): void {
    this.rootBranchingNodes.clear();
    this.getRootBranchingNodesHelper('trigger', 0, new Set<string>());
  }

  // A root branch is a branch step that is not nested inside another branch step. I.E. a top level branch.
  private getRootBranchingNodesHelper(nodeId: string, distanceFromRoot: number, visitedNodes: Set<string>): void {
    if (visitedNodes.has(nodeId)) {
      return;
    }
    visitedNodes.add(nodeId);

    const children = this.getChildrenNodes(nodeId);
    if (!children?.length) {
      return;
    }
    // We are a merge step, so are closer to the root
    if (this.getParentNodes(nodeId)!.length > 1) {
      distanceFromRoot--;
    }
    // Follow the single child
    if (children.length === 1) {
      return this.getRootBranchingNodesHelper(children[0], distanceFromRoot, visitedNodes);
    }
    // we have multiple children (are a branch)
    if (distanceFromRoot === 0) {
      this.rootBranchingNodes.add(nodeId);
    }
    // Each child will be 1 step further away from the root. Follow them.
    for (let i = 0; i < children.length; i++) {
      this.getRootBranchingNodesHelper(children[i], distanceFromRoot + 1, visitedNodes);
    }
    return;
  }

  // if all parents of the end step are hidden or terminating, then we can remove the end step so that it is not drawn
  private optionallyRemoveEndStep(): void {
    const parents = this.getParentNodes(END_STEP_ID);
    if (!parents?.length) {
      return;
    }
    const allParentsAreHidden = parents.every(
      (parent) => parent.startsWith(HIDDEN_STEP_PREFIX) || this.terminatingStepIds.includes(parent),
    );
    if (allParentsAreHidden) {
      this.graph.removeNode(END_STEP_ID);
    }
  }

  private generateExpandedGroupStepId(stepID: string, start: boolean) {
    if (start) {
      return EXPANDED_GROUP_START_PREFIX + stepID;
    }

    return EXPANDED_GROUP_END_PREFIX + stepID;
  }

  // Generate unique ID for an Add Step node
  private generateAddStepId(): string {
    return ADD_STEP_PREFIX + uuidv4();
  }

  // Generate unique ID for a hidden step node
  private generateHiddenStepId(): string {
    return HIDDEN_STEP_PREFIX + uuidv4();
  }

  // Generate unique ID for a passthrough step node
  private generatePassthroughStepId(): string {
    return PASSTHROUGH_STEP_PREFIX + uuidv4();
  }

  // Build ID for a group step node
  private buildGroupStepId(groupId: string, expanded?: boolean): string {
    if (!groupId || expanded) {
      return '';
    }
    return GROUP_STEP_PREFIX + groupId;
  }

  // traceNodeUpToTheBranchNodeIAmInside method Traces a node up the graph to find the branch node it is inside, prioritizing a specified parent path.
  // parentToFollow is used to prioritize a particular path when there are multiple parents
  public traceNodeUpToTheBranchNodeIAmInside(
    nodeId: string,
    initDistance?: number,
    stopIfMergeIsFirst?: boolean,
    parentToFollow?: string,
  ): TracedBranchNode | null {
    let current = nodeId;
    let distance = initDistance || 0;
    // for (;;) is the same as while(true) but without a lint error
    for (;;) {
      const parents = this.getParentNodes(current);
      if (!parents?.length) {
        return null; // We have reached the top of the graph
      }

      const parent = parents.find((parent) => parent === parentToFollow) || parents[0];

      const childrenNodes: string[] = this.getChildrenNodes(parent);
      if (childrenNodes?.length > 1 && distance === 0) {
        return { nodeId: parent, numChildren: childrenNodes.length, indexWeCameFrom: childrenNodes.indexOf(current) };
      }
      if (childrenNodes?.length > 1) {
        distance--;
      }

      if (parents.length > 1) {
        distance++;
      }

      if (distance > 0 && stopIfMergeIsFirst) {
        // For terminating steps, if we hit a merge first it means we are not inside a branch
        return null;
      }

      current = parent;
    }
  }

  // Get all the edges in the graph
  public getAllEdges(): Edge[] {
    return this.graph.edges();
  }

  // Get all nodeIds in the graph
  public getAllNodes(): string[] {
    return this.graph.nodes();
  }

  // Get the nodes that this node is pointing to
  public getChildrenNodes(stepId: string): string[] {
    return this.graph.successors(stepId) || [];
  }

  // Get the nodes that this node is being pointed at by
  public getParentNodes(stepId: string): string[] {
    return this.graph.predecessors(stepId) || [];
  }

  public getTheStartingNode(stepId: string): string {
    const parents = this.getParentNodes(stepId);
    if (!parents?.length) {
      return stepId;
    }
    return this.getTheStartingNode(parents?.[0]);
  }
}
